import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DepreciationRow,
  DepreciationStorePreference,
  DepreciationTable,
  DepreciationTableSummary,
} from 'src/types';
import { queryClient } from '..';
import { DELETE_KEY, DEPRECIATION_TABLE_QUERY_KEY, ENTITY_KEY, LIST_KEY, POST_KEY, PUT_KEY } from '../queryKeys';
import { queryYemboApiCall, queryYemboApiCallWithReturnValue } from '../queryYemboApiCalls';

/**
 * A company's depreciation tables, from service-voyager-api's
 * `/depreciation-table` routes. The list carries each table's `rowCount`; the
 * rows themselves come with a single table.
 */

const URL = 'depreciation-table';

const listKey = (companyKey?: string) => [DEPRECIATION_TABLE_QUERY_KEY, LIST_KEY, companyKey];
const entityKey = (key?: string) => [DEPRECIATION_TABLE_QUERY_KEY, ENTITY_KEY, key];

/** Every table of one company, in name order. Held until `companyKey` is known. */
export const useDepreciationTablesQuery = (companyKey?: string) =>
  useQuery({
    queryKey: listKey(companyKey),
    queryFn: async ({ signal }) =>
      (await queryYemboApiCallWithReturnValue<DepreciationTableSummary[], 'depreciationTables'>({
        url: URL,
        urlParams: new URLSearchParams({ companyKey: companyKey ?? '' }),
        key: 'depreciationTables',
        shouldNotifyAndThrowError: true,
        signal,
      })) ?? [],
    enabled: !!companyKey,
  });

/**
 * Every category across the company's tables, each once, in the order the API
 * matches items against them (its own tables first, "Standard Contents"
 * last). The video pipeline categorizes items into exactly these, and an item
 * whose category is one of them is depreciated with that row. Under the list
 * key, so a table write refreshes it too.
 */
export const useDepreciationCategoriesQuery = (companyKey?: string) =>
  useQuery({
    queryKey: [DEPRECIATION_TABLE_QUERY_KEY, LIST_KEY, 'categories', companyKey],
    queryFn: async ({ signal }) =>
      (await queryYemboApiCallWithReturnValue<string[], 'categories'>({
        url: `${URL}/categories`,
        urlParams: new URLSearchParams({ companyKey: companyKey ?? '' }),
        key: 'categories',
        signal,
      })) ?? [],
    enabled: !!companyKey,
    staleTime: 5 * 60 * 1000,
  });

/** `GET /depreciation-table/:key` answers the table itself, rows included. */
const getDepreciationTable = async (key: string, signal?: AbortSignal) =>
  queryYemboApiCallWithReturnValue<DepreciationTable>({
    url: `${URL}/${key}`,
    shouldNotifyAndThrowError: true,
    signal,
  });

/** One table with its rows; null when it does not exist (or was deleted). */
export const useDepreciationTableQuery = (key?: string) =>
  useQuery({
    queryKey: entityKey(key),
    queryFn: ({ signal }) => getDepreciationTable(key ?? '', signal),
    enabled: !!key,
  });

/** One table with its rows, from the cache when it is fresh — for exporting from the list. */
export const fetchDepreciationTable = (key: string) =>
  queryClient.fetchQuery({ queryKey: entityKey(key), queryFn: ({ signal }) => getDepreciationTable(key, signal) });

/** POST (201) and PUT answer the table itself. */
const readTable = async (response: Response) => (await response.json()) as DepreciationTable;

/** After any write: refresh the lists, and seed the written table's entry so opening it needs no request. */
const afterWrite = (table?: DepreciationTable) => {
  if (table) queryClient.setQueryData(entityKey(table.key), table);
  return queryClient.invalidateQueries({ queryKey: [DEPRECIATION_TABLE_QUERY_KEY, LIST_KEY] });
};

/** Create a table, empty or with the rows of an imported workbook. The server refuses a name already in use. */
export const useCreateDepreciationTable = () =>
  useMutation({
    mutationKey: [DEPRECIATION_TABLE_QUERY_KEY, POST_KEY],
    mutationFn: async (variables: { companyKey: string; name: string; rows: DepreciationRow[] }) =>
      readTable(
        await queryYemboApiCall({
          url: URL,
          method: 'POST',
          body: JSON.stringify(variables),
          shouldNotifyAndThrowError: true,
          logoutIfNotAuthorized: false,
        })
      ),
    onSuccess: afterWrite,
  });

/**
 * Rename a table, replace all of its rows (as an import does), and/or replace
 * its whole list of preferred stores by group or category (Admin → Shopping),
 * each optional and sent alone if need be.
 */
export const useUpdateDepreciationTable = () =>
  useMutation({
    mutationKey: [DEPRECIATION_TABLE_QUERY_KEY, PUT_KEY],
    mutationFn: async ({
      key,
      ...changes
    }: {
      key: string;
      name?: string;
      rows?: DepreciationRow[];
      storePreferences?: DepreciationStorePreference[];
    }) =>
      readTable(
        await queryYemboApiCall({
          url: `${URL}/${key}`,
          method: 'PUT',
          body: JSON.stringify(changes),
          shouldNotifyAndThrowError: true,
          logoutIfNotAuthorized: false,
        })
      ),
    onSuccess: afterWrite,
  });

export const useDeleteDepreciationTable = () =>
  useMutation({
    mutationKey: [DEPRECIATION_TABLE_QUERY_KEY, DELETE_KEY],
    mutationFn: async ({ key }: { key: string }) => {
      await queryYemboApiCall({
        url: `${URL}/${key}`,
        method: 'DELETE',
        shouldNotifyAndThrowError: true,
        logoutIfNotAuthorized: false,
      });
      queryClient.removeQueries({ queryKey: entityKey(key) });
    },
    onSuccess: () => afterWrite(),
  });
